import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Button,
    Chip,
    Alert,
    IconButton,
    Tooltip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import {
    Code as CodeIcon,
    Add as AddIcon,
    Download as DownloadIcon,
    PlayArrow as PlayArrowIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import DateRangePicker from '../../../components/DateRangePicker';
import AddSdkModal from './AddSdkModal';
import TestSdkModal from './TestSdkModal';
import vneidSdkService from '../../../services/vneidSdkService';
import unitService from '../../../services/unitService';
import {
    fetchSdkList,
    fetchInitData,
    createSdk,
    updateSdk,
    deleteSdk,
    setPage,
    setPageSize,
    fetchSdkLogs,
    setLogsPage,
    setLogsPageSize,
    setLogsFilters,
    clearError,
    clearSuccessMessage,
    selectSdkList,
    selectTotal,
    selectPage,
    selectPageSize,
    selectInitData,
    selectListLoading,
    selectCreateLoading,
    selectUpdateLoading,
    selectError,
    selectSuccessMessage,
    selectSdkLogs,
    selectLogsTotal,
    selectLogsPage,
    selectLogsPageSize,
    selectLogsFilters,
    selectLogsLoading,
} from './sliceTab4';

export default function Tab4SdkApiManagement() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Local state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [sdkToEdit, setSdkToEdit] = useState(null);
    const [units, setUnits] = useState([]);

    // Redux state - SDK List
    const sdkList = useSelector(selectSdkList);
    const total = useSelector(selectTotal);
    const page = useSelector(selectPage);
    const pageSize = useSelector(selectPageSize);
    const initData = useSelector(selectInitData);
    const listLoading = useSelector(selectListLoading);
    const createLoading = useSelector(selectCreateLoading);
    const updateLoading = useSelector(selectUpdateLoading);
    const error = useSelector(selectError);
    const successMessage = useSelector(selectSuccessMessage);

    // Redux state - SDK Logs
    const logs = useSelector(selectSdkLogs);
    const logsTotal = useSelector(selectLogsTotal);
    const logsPage = useSelector(selectLogsPage);
    const logsPageSize = useSelector(selectLogsPageSize);
    const logsFilters = useSelector(selectLogsFilters);
    const logsLoading = useSelector(selectLogsLoading);

    // Fetch data on mount
    useEffect(() => {
        dispatch(fetchInitData());
        dispatch(fetchSdkList());
        dispatch(fetchSdkLogs());
    }, [dispatch]);

    // Fetch units for filter dropdown
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await unitService.getUnitInit();
                setUnits(response.units || []);
            } catch (err) {
                console.error('Error fetching units:', err);
            }
        };
        fetchUnits();
    }, []);

    // Refetch SDK list when page/pageSize changes
    useEffect(() => {
        dispatch(fetchSdkList());
    }, [dispatch, page, pageSize]);

    // Refetch logs when page/pageSize/filters change
    useEffect(() => {
        dispatch(fetchSdkLogs());
    }, [dispatch, logsPage, logsPageSize, logsFilters]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (successMessage || error) {
            const timer = setTimeout(() => {
                dispatch(clearSuccessMessage());
                dispatch(clearError());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage, error, dispatch]);

    // Handlers
    const handleAddSdk = () => {
        setShowAddModal(true);
    };

    const handleEditSdk = (sdk) => {
        setSdkToEdit(sdk);
        setShowEditModal(true);
    };

    const handleDeleteSdk = (sdk) => {
        if (confirm(t('vneid.sdk.deleteConfirm', { version: sdk.version }))) {
            dispatch(deleteSdk(sdk.id));
        }
    };

    const handleDownloadSdk = async (sdk) => {
        try {
            await vneidSdkService.downloadSdkFile(sdk.id, sdk.fileName);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    const handleAddConfirm = (formData) => {
        dispatch(createSdk(formData));
    };

    const handleEditConfirm = (data) => {
        dispatch(updateSdk(data));
    };

    // Get status display name
    const getStatusName = (statusId) => {
        const status = (initData.statuses || []).find(s => s.id === statusId);
        return status ? status.name : statusId;
    };

    // Get method display name
    const getMethodName = (methodId) => {
        const method = (initData.methods || []).find(m => m.id === methodId);
        return method ? method.name : methodId;
    };

    // Get security display name
    const getSecurityName = (securityId) => {
        const security = (initData.securities || []).find(s => s.id === securityId);
        return security ? security.name : securityId;
    };

    // Get status color
    const getStatusColor = (statusId) => {
        switch (statusId) {
            case 0:
                return 'default';
            case 1:
                return 'success';
            case 2:
                return 'warning';
            case 3:
                return 'error';
            default:
                return 'default';
        }
    };

    // Table columns - SDK List
    const columns = [
        {
            key: 'version',
            label: t('vneid.sdk.col.version'),
            sortable: true,
        },
        {
            key: 'fileName',
            label: t('vneid.sdk.col.fileName'),
            sortable: true,
        },
        {
            key: 'description',
            label: t('vneid.sdk.col.description'),
            sortable: false,
            render: (value) => value || '-',
        },
        {
            key: 'methodId',
            label: t('vneid.sdk.col.method'),
            sortable: false,
            render: (value) => getMethodName(value),
        },
        {
            key: 'securityId',
            label: t('vneid.sdk.col.security'),
            sortable: false,
            render: (value) => getSecurityName(value),
        },
        {
            key: 'status',
            label: t('vneid.sdk.col.status'),
            sortable: false,
            render: (value) => (
                <Chip
                    label={getStatusName(value)}
                    color={getStatusColor(value)}
                    size="small"
                />
            ),
        },
        {
            key: 'createdOn',
            label: t('vneid.sdk.col.createdOn'),
            sortable: true,
            render: (value) => value ? new Date(value).toLocaleDateString('vi-VN') : '-',
        },
    ];

    // Table columns - SDK Logs
    const logsColumns = [
        {
            key: 'unitName',
            label: t('vneid.sdk.logs.col.unitName'),
        },
        {
            key: 'key',
            label: t('vneid.sdk.logs.col.key'),
        },
        {
            key: 'version',
            label: t('vneid.sdk.logs.col.version'),
        },
        {
            key: 'success',
            label: t('vneid.sdk.logs.col.success'),
            render: (value) => (
                <Chip
                    label={value ? t('vneid.sdk.logs.successTrue') : t('vneid.sdk.logs.successFalse')}
                    color={value ? 'success' : 'error'}
                    size="small"
                />
            ),
        },
        {
            key: 'message',
            label: t('vneid.sdk.logs.col.message'),
            render: (value) => value || '-',
        },
        {
            key: 'ipAddress',
            label: t('vneid.sdk.logs.col.ipAddress'),
        },
        {
            key: 'createdOn',
            label: t('vneid.sdk.logs.col.createdOn'),
            render: (value) => value ? new Date(value).toLocaleString('vi-VN') : '-',
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon color="primary" />
                    {t('vneid.stats.tab4')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => setShowTestModal(true)}
                        id="testSdk"
                        name="testSdk"
                        data-testid="sdk-button-testSdk"
                    >
                        {t('vneid.sdk.testSdk.button')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddSdk}
                        id="addSdk"
                        name="addSdk"
                        data-testid="sdk-button-addSdk"
                    >
                        {t('vneid.sdk.addSdk')}
                    </Button>
                </Box>
            </Box>

            {/* Messages */}
            {successMessage && (
                <Alert severity="success" onClose={() => dispatch(clearSuccessMessage())}>
                    {t(successMessage)}
                </Alert>
            )}
            {error && (
                <Alert severity="error" onClose={() => dispatch(clearError())}>
                    {error}
                </Alert>
            )}

            {/* Table */}
            <Table
                id="sdk-table"
                columns={columns}
                data={sdkList}
                loading={listLoading}
                pagination={true}
                page={page}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={(newPage) => dispatch(setPage(newPage))}
                onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
                renderActions={(row) => (
                    <>
                        <Tooltip title={t('vneid.sdk.download')}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleDownloadSdk(row)}
                                id={`download-${row.id}`}
                                name={`download-${row.id}`}
                                data-testid={`sdk-button-download-${row.id}`}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.edit')}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditSdk(row)}
                                id={`edit-${row.id}`}
                                name={`edit-${row.id}`}
                                data-testid={`sdk-button-edit-${row.id}`}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteSdk(row)}
                                id={`delete-${row.id}`}
                                name={`delete-${row.id}`}
                                data-testid={`sdk-button-delete-${row.id}`}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                emptyMessage={t('vneid.sdk.noData')}
            />

            {/* Add Modal */}
            <AddSdkModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                onConfirm={handleAddConfirm}
                editMode={false}
                loading={createLoading}
                initData={initData}
            />

            {/* Edit Modal */}
            <AddSdkModal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSdkToEdit(null);
                }}
                onConfirm={handleEditConfirm}
                sdkData={sdkToEdit}
                editMode={true}
                loading={updateLoading}
                initData={initData}
            />

            {/* Test SDK Modal */}
            <TestSdkModal
                open={showTestModal}
                onClose={() => setShowTestModal(false)}
            />

            {/* Divider */}
            <Divider sx={{ my: 2 }} />

            {/* SDK Logs Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Logs Header */}
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" />
                    {t('vneid.sdk.logs.title')}
                </Typography>

                {/* Logs Filters */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="logs-filter-unit-label">{t('vneid.sdk.logs.filterByUnit')}</InputLabel>
                        <Select
                            labelId="logs-filter-unit-label"
                            value={logsFilters.unitId || ''}
                            onChange={(e) => dispatch(setLogsFilters({ unitId: e.target.value || '' }))}
                            label={t('vneid.sdk.logs.filterByUnit')}
                            inputProps={{
                                id: 'logsFilterUnit',
                                name: 'logsFilterUnit',
                                'data-testid': 'sdk-logs-select-filterUnit',
                            }}
                        >
                            <MenuItem value="">{t('vneid.sdk.logs.allUnits')}</MenuItem>
                            {units.map((unit) => (
                                <MenuItem key={unit.id} value={unit.id}>
                                    {unit.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <DateRangePicker
                        label={t('vneid.sdk.logs.dateRange')}
                        fromValue={logsFilters.fromDate || ''}
                        toValue={logsFilters.toDate || ''}
                        onFromChange={(e) => dispatch(setLogsFilters({ fromDate: e.target.value || null }))}
                        onToChange={(e) => dispatch(setLogsFilters({ toDate: e.target.value || null }))}
                        id="logsDateRange"
                        name="logsDateRange"
                        data-testid="sdk-logs-daterangepicker"
                    />
                </Box>

                {/* Logs Table */}
                <Table
                    id="sdk-logs-table"
                    columns={logsColumns}
                    data={logs}
                    loading={logsLoading}
                    pagination={true}
                    page={logsPage}
                    pageSize={logsPageSize}
                    totalItems={logsTotal}
                    onPageChange={(newPage) => dispatch(setLogsPage(newPage))}
                    onPageSizeChange={(newSize) => dispatch(setLogsPageSize(newSize))}
                    emptyMessage={t('vneid.sdk.logs.noData')}
                />
            </Box>
        </Box>
    );
}
